import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  UserGroupIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  PlayIcon,
  LockClosedIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const P2PTrading = () => {
  const features = [
    {
      icon: UserGroupIcon,
      title: 'Direct Trading',
      description: 'Trade directly with other users without intermediaries. Set your own rates and terms.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Secure Escrow',
      description: 'All trades are protected by our secure escrow system. Funds are held safely until both parties confirm.'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Flexible Rates',
      description: 'Set your own exchange rates or accept the best available rates from other traders.'
    },
    {
      icon: ClockIcon,
      title: 'Fast Settlement',
      description: 'Quick transaction processing with instant notifications and real-time status updates.'
    },
    {
      icon: LockClosedIcon,
      title: 'Bank-Level Security',
      description: 'Military-grade encryption and multi-factor authentication to protect your transactions.'
    },
    {
      icon: ChartBarIcon,
      title: 'Market Insights',
      description: 'Access real-time market rates and trading volume data to make informed decisions.'
    }
  ];

  const benefits = [
    {
      title: 'Lower Fees',
      description: 'Trade directly with peers and avoid high exchange fees. Save up to 80% on transaction costs.',
      icon: CurrencyDollarIcon
    },
    {
      title: 'Better Rates',
      description: 'Negotiate rates directly with other traders. Often better than traditional exchanges.',
      icon: ChartBarIcon
    },
    {
      title: 'Privacy',
      description: 'Trade with enhanced privacy. No need to share all your personal information with exchanges.',
      icon: LockClosedIcon
    },
    {
      title: '24/7 Support',
      description: 'Round-the-clock customer support to help resolve any disputes or issues quickly.',
      icon: ClockIcon
    }
  ];

  const stats = [
    { label: 'Active Traders', value: '25,000+' },
    { label: 'Daily Volume', value: '$2.5M+' },
    { label: 'Success Rate', value: '98.5%' },
    { label: 'Avg Fee', value: '0.5%' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-to-br from-slate-950 via-slate-900 to-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full">
                  <span className="text-amber-300 text-sm font-medium">Peer-to-Peer</span>
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  P2P Trading
                  <span className="block bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent mt-2">
                    Trade Directly
                  </span>
                </h1>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Trade directly with other users in a secure, peer-to-peer environment. 
                  Set your own rates, enjoy lower fees, and maintain full control of your trades.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-amber-500/50"
                >
                  Start Trading
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <button className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-amber-500/50 text-white font-semibold text-lg rounded-xl transition-all duration-300">
                  <PlayIcon className="mr-2 h-5 w-5" />
                  Watch Demo
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-gray-800">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl lg:text-3xl font-bold text-amber-400 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border-2 border-amber-500/30 shadow-2xl">
                <img
                  src="/images/brokers-shaking-hands-with-successful-deal_236854-49521.jpg"
                  alt="P2P Trading"
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.src = '/images/21489.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">P2P</div>
                    <div className="text-xs text-gray-300">Trading</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Why Choose P2P Trading?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Experience the benefits of direct peer-to-peer trading
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white rounded-2xl border-2 border-gray-200 p-8 hover:border-amber-500 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-200 transition-colors duration-300">
                  <feature.icon className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 lg:py-32 bg-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Key Benefits
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Discover why thousands of traders prefer P2P trading
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl border-2 border-amber-500/30 p-8 hover:border-amber-500/50 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <benefit.icon className="w-7 h-7 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">{benefit.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              How It Works
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Create Offer', desc: 'Post your trading offer with your preferred rates and payment methods' },
              { step: '2', title: 'Match & Confirm', desc: 'Find matching offers or wait for traders to accept your offer' },
              { step: '3', title: 'Secure Escrow', desc: 'Funds are held in secure escrow until both parties confirm the trade' },
              { step: '4', title: 'Complete Trade', desc: 'Once confirmed, funds are released and the trade is complete' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-amber-600">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-amber-50 to-yellow-50 overflow-hidden">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Start P2P Trading Today
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Join our growing community of P2P traders and enjoy lower fees and better rates
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get Started Free
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/markets"
                className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-amber-500 text-gray-900 font-semibold text-lg rounded-xl transition-all duration-300"
              >
                Browse Offers
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default P2PTrading;

